import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useConsent } from '../../contexts/ConsentContext';
import {
  getInstallSnapshot,
  requestPwaInstall,
  subscribeInstall,
  type InstallResult,
} from '../../lib/pwaInstall';
import styles from './PwaInstallBanner.module.css';

const STORAGE_KEY = 'buzzmap_pwa_install_minimized';

function isAppleMobile(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/i.test(ua) ||
    (/(Macintosh|MacIntel)/i.test(`${ua} ${navigator.platform}`) && navigator.maxTouchPoints > 1);
}

function isEmbeddedBrowser(): boolean {
  return /FBAN|FBAV|Instagram|TikTok|Line\/|Snapchat|Twitter|\bwv\b/i.test(navigator.userAgent);
}

function readMinimized(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveMinimized(value: boolean): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // The banner remains usable when browser storage is blocked.
  }
}

export function PwaInstallBanner() {
  const { showBanner: showCookieBanner } = useConsent();
  const { canPrompt, installed, standalone } = useSyncExternalStore(
    subscribeInstall,
    getInstallSnapshot
  );
  const [minimized, setMinimized] = useState(readMinimized);
  const [consentReady, setConsentReady] = useState(false);
  const [result, setResult] = useState<InstallResult | null>(null);
  const [requesting, setRequesting] = useState(false);
  const requestInProgress = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    setConsentReady(true);
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (canPrompt) setResult(null);
  }, [canPrompt]);
  const appleMobile = isAppleMobile();
  const android = /Android/i.test(navigator.userAgent);
  const embedded = isEmbeddedBrowser();

  if (!consentReady || installed || standalone || showCookieBanner || (!appleMobile && !android && !canPrompt && !requesting && !result)) {
    return null;
  }

  const toggle = () => {
    setMinimized((current) => {
      saveMinimized(!current);
      return !current;
    });
  };

  const install = () => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    setRequesting(true);
    setResult(null);
    void requestPwaInstall().then((outcome) => {
      if (mounted.current) setResult(outcome);
    }).finally(() => {
      requestInProgress.current = false;
      if (mounted.current) setRequesting(false);
    });
  };

  return (
    <aside className={`${styles.banner} ${minimized ? styles.minimized : ''}`} aria-label="Installazione di Buzzmap">
      <div className={styles.header}>
        <h2 className={styles.heading} hidden={minimized}>Buzzmap a portata di mano</h2>
        <button
          type="button"
          className={minimized ? styles.reopen : styles.close}
          onClick={toggle}
          aria-label={minimized ? undefined : 'Chiudi invito installazione'}
          aria-expanded={!minimized}
          aria-controls="pwa-install-details"
        >
          {minimized ? 'Installa app · istruzioni' : <span aria-hidden="true">×</span>}
        </button>
      </div>
      <div id="pwa-install-details" hidden={minimized} className={styles.details}>
        <p className={styles.description}>Aggiungi Buzzmap alla Home.</p>
        {(canPrompt || requesting) && (
          <button type="button" className={styles.install} onClick={install} disabled={requesting}>
            {requesting ? 'Installazione in corso…' : 'Installa app'}
          </button>
        )}
        {result === 'error' && (
          <p role="alert" className={styles.message}>
            Non è stato possibile aprire la richiesta di installazione. Prova dal menu del browser.
          </p>
        )}
        {result === 'dismissed' && (
          <p role="status" className={styles.message}>
            Richiesta chiusa. Puoi usare il menu del browser o riprovare quando l’opzione torna disponibile.
          </p>
        )}
        {result === 'accepted' && (
          <p role="status" className={styles.message}>
            Richiesta accettata. Controlla la schermata Home per aprire l’app.
          </p>
        )}
        {appleMobile ? (
          <div className={styles.instructions}>
            <p>Dal menu Condividi scegli “Aggiungi alla schermata Home”, poi “Apri come app web” se compare e infine “Aggiungi”.</p>
            <p>Se l’opzione manca, apri la pagina in Safari e riprova.</p>
            {embedded && <p>Se sei in un browser incorporato in un’altra app, apri prima la pagina nel browser.</p>}
          </div>
        ) : android && !canPrompt && !requesting ? (
          <div className={styles.instructions}>
            <p>Apri il menu del browser e cerca “Installa app” o “Aggiungi alla schermata Home”, se disponibile.</p>
            {embedded && <p>Se sei in un browser incorporato in un’altra app, prova ad aprire la pagina in Chrome.</p>}
          </div>
        ) : result && !android ? (
          <p className={styles.instructions}>Puoi controllare il menu del browser per un’opzione di installazione.</p>
        ) : null}
      </div>
    </aside>
  );
}
